/**
 * CP6PublicPage — public share page link.
 * data-screen-label="cp-06 Public share-page"
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import { CPBackdrop } from './shared';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

interface CP6PublicPageProps {
  flow: UseCoupleFlowReturn;
  onBack: () => void;
}

export function CP6PublicPage({ flow, onBack }: CP6PublicPageProps): JSX.Element {
  const { t } = useTranslation('flows');
  const [copied, setCopied] = useState(false);

  const shortId = flow.couplePortraitId?.slice(0, 8) ?? '…';
  const url = flow.couplePortraitId
    ? `https://calestea.com/couple/${flow.couplePortraitId}`
    : null;

  function handleCopy(): void {
    if (!url) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleOpen(): void {
    if (!url) return;
    try {
      WebApp.openLink(url);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <div className={styles.root} data-screen-label="cp-06 Public share-page">
      <CPBackdrop tone="quiet" />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('couple.headerTitle')} onBack={onBack} headingId="couple-heading" />

        {/* Title */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{t('couple.publicPage.eyebrow')}</div>
          <h1 className={styles.displayTitleCenter}>
            {t('couple.publicPage.titlePrefix')} <em className={styles.gradAccent}>{t('couple.publicPage.titleAccent')}</em>
          </h1>
        </div>

        {/* Browser chrome mock */}
        <div className={styles.browserChrome} style={{ marginTop: 24 }}>
          <div className={styles.browserAddressBar}>
            <div className={styles.browserAddressDot} />
            <span>calestea.com/couple/{shortId}</span>
          </div>

          {/* URL copy field */}
          <div className={styles.copyLinkRow}>
            <span className={styles.copyLinkText}>
              {url ?? t('couple.publicPage.notAvailable')}
            </span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={handleCopy}
              disabled={!url}
            >
              {copied ? `✓ ${t('couple.publicPage.copied')}` : t('couple.publicPage.copyLink')}
            </button>
          </div>

          <div className={styles.noteBanner}>
            {t('couple.publicPage.note')}
          </div>
        </div>

        {/* Open in browser */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            type="button"
            onClick={handleOpen}
            disabled={!url}
          >
            {t('couple.publicPage.openCta')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
